import * as React from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Text,
  Title1,
  makeStyles,
  tokens,
  Card,
  CardHeader,
  Checkbox,
} from "@fluentui/react-components";
import {
  Dismiss16Regular,
  ChevronLeft16Regular,
  ChevronRight16Regular,
} from "@fluentui/react-icons";
import { getDialogSize } from "../dialogs/dialogDisplay";

// Declare Office as global (available in Office Add-in context)
declare const Office: any;

const useFirstRunStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100vh",
    backgroundColor: tokens.colorNeutralBackground1,
    padding: "0",
    margin: "0",
    overflow: "visible", // Changed from "hidden" to "visible" for macOS compatibility
    position: "relative", // Ensure proper positioning context
  },
  carouselContainer: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "center",
    padding: tokens.spacingVerticalM,
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalM,
  },
  slide: {
    width: "100%",
    maxWidth: "400px",
    height: "auto",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    justifyContent: "flex-start",
  },
  slideImage: {
    width: "100%",
    maxWidth: "100%", // Changed to full width
    height: "auto",
    marginBottom: tokens.spacingVerticalM,
    borderRadius: tokens.borderRadiusMedium,
    boxShadow: tokens.shadow4,
    minHeight: "180px", // Reduced minimum height
    backgroundColor: tokens.colorNeutralBackground3, // Background while loading
    objectFit: "contain",
  },
  imageContainer: {
    position: "relative",
    width: "100%",
    marginBottom: tokens.spacingVerticalM,
    cursor: "pointer",
  },
  imageLabel: {
    position: "absolute",
    bottom: "10px",
    left: "50%",
    transform: "translateX(-50%)",
    backgroundColor: "rgba(0, 0, 0, 0.8)",
    color: "white",
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusSmall,
    fontSize: "12px",
    fontWeight: "500",
    boxShadow: tokens.shadow4,
    transition: "opacity 0.3s ease",
    opacity: 0.9,
    ":hover": {
      opacity: 1,
    },
  },
  imagePlaceholder: {
    width: "100%",
    maxWidth: "300px",
    height: "200px",
    marginBottom: tokens.spacingVerticalL,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground3,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: `1px dashed ${tokens.colorNeutralStroke2}`,
    color: tokens.colorNeutralForeground2,
    fontSize: "14px",
    textAlign: "center",
  },
  placeholderSubtext: {
    fontSize: "12px",
    opacity: 0.7,
  },
  slideTitle: {
    marginBottom: tokens.spacingVerticalS,
    color: tokens.colorBrandForeground1,
    fontSize: "20px",
    fontWeight: "600",
  },
  slideDescription: {
    marginBottom: tokens.spacingVerticalXL,
    color: tokens.colorNeutralForeground2,
    fontSize: "14px",
    lineHeight: "1.5",
    maxWidth: "320px",
  },
  navigation: {
    display: "flex",
    justifyContent: "space-between", // Space between buttons and dots
    alignItems: "center",
    width: "100%",
    maxWidth: "400px",
    paddingTop: tokens.spacingVerticalM,
  },
  navButton: {
    minWidth: "100px",
    height: "36px",
  },
  dots: {
    display: "flex",
    gap: tokens.spacingHorizontalXS,
    alignItems: "center",
  },
  dot: {
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    backgroundColor: tokens.colorNeutralStroke2,
    cursor: "pointer",
    transition: "all 0.2s ease",
  },
  activeDot: {
    backgroundColor: tokens.colorBrandBackgroundPressed,
    transform: "scale(1.25)",
  },
  getStartedButton: {
    minWidth: "120px",
    height: "40px",
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    ":hover": {
      backgroundColor: tokens.colorBrandBackgroundHover,
    },
  },
  getStartedContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    paddingTop: tokens.spacingVerticalL,
    paddingBottom: tokens.spacingVerticalM,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    marginTop: tokens.spacingVerticalM,
    gap: tokens.spacingVerticalM,
  },
  checkboxContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  checkboxLabel: {
    fontSize: "13px",
    color: tokens.colorNeutralForeground2,
  },
  // Modal/Popup styles
  modalOverlay: {
    position: "fixed",
    top: "0",
    left: "0",
    right: "0",
    bottom: "0",
    backgroundColor: "rgba(0, 0, 0, 0.8)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 100, // Reduced from 1000 to 100 to avoid Excel UI conflicts
    padding: tokens.spacingVerticalM,
  },
  modalContent: {
    position: "relative",
    maxWidth: "50.625vw", // 67.5vw zmniejszone o kolejne 25%
    maxHeight: "50.625vh", // 67.5vh zmniejszone o kolejne 25%
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge,
    padding: tokens.spacingVerticalL,
    boxShadow: tokens.shadow64,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  modalImage: {
    maxWidth: "100%",
    maxHeight: "39.375vh", // 52.5vh zmniejszone o kolejne 25%
    objectFit: "contain",
    borderRadius: tokens.borderRadiusMedium,
  },
  modalTitle: {
    marginTop: tokens.spacingVerticalM,
    marginBottom: tokens.spacingVerticalS,
    textAlign: "center",
    color: tokens.colorBrandForeground1,
  },
  modalDescription: {
    marginBottom: tokens.spacingVerticalL,
    textAlign: "center",
    color: tokens.colorNeutralForeground2,
    maxWidth: "225px", // 300px zmniejszone o kolejne 25%
  },
  closeButton: {
    position: "absolute",
    top: tokens.spacingVerticalS,
    right: tokens.spacingVerticalS,
    minWidth: "auto",
    padding: tokens.spacingVerticalXS,
  },
  clickableImage: {
    cursor: "pointer",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
    ":hover": {
      transform: "scale(1.02)",
      boxShadow: tokens.shadow8,
    },
  },
});

interface FirstRunExperienceProps {
  onComplete: () => void;
  onDisable: () => Promise<void>;
  showHeader?: boolean; // Optional prop to control Header visibility
  variant?: 'startup' | 'info'; // 'startup' = first app start, 'info' = called from InfoPage
}

interface SlideData {
  image: string;
  titleKey: string;
  descriptionKey: string;
}

const FirstRunExperience: React.FC<FirstRunExperienceProps> = ({
  onComplete,
  onDisable,
  showHeader = false, // Default to false (Header hidden) unless explicitly set to true
  variant = 'info', // Default to 'info' variant (called from InfoPage) for backward compatibility
}) => {
  const styles = useFirstRunStyles();
  const { t } = useTranslation();
  const [currentSlide, setCurrentSlide] = React.useState(0);
  const [imageLoadError, setImageLoadError] = React.useState<Set<number>>(new Set());
  const [dontShowAgain, setDontShowAgain] = React.useState(() => {
    // Initialize from localStorage - check if user previously selected "don't show again"
    try {
      const saved = localStorage.getItem('carbowaste-firstrun-dont-show-again');
      return saved === 'true';
    } catch (error) {
      console.warn('Failed to read firstrun checkbox state from localStorage:', error);
      return false;
    }
  });
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [isOfficeDialogOpen, setIsOfficeDialogOpen] = React.useState(false);
  const officeDialogRef = React.useRef<any>(null);

  // Debug tracking for macOS stability issues
  const mountTimeRef = React.useRef<number>(Date.now());
  const renderCountRef = React.useRef<number>(0);
  const isInitializedRef = React.useRef<boolean>(false);

  // Detect macOS for enhanced debugging
  const isMacOS = React.useMemo(() => {
    const userAgent = navigator.userAgent;
    const platform = navigator.platform;
    return platform.toUpperCase().indexOf('MAC') >= 0 ||
      userAgent.includes('Mac') ||
      userAgent.includes('Macintosh');
  }, []);

  // Initialize component only once
  React.useEffect(() => {
    if (!isInitializedRef.current) {
      isInitializedRef.current = true;
      if (isMacOS) {
        console.log('🍎 [macOS] FirstRunExperience initialized - setting up macOS-specific safeguards');
      }
    }
  }, [isMacOS]);

  const slides: SlideData[] = [
    {
      image: "/assets/images/01.gif",
      titleKey: "firstrun_slide0_title",
      descriptionKey: "firstrun_slide0_description",
    },
    {
      image: "/assets/images/02.gif",
      titleKey: "firstrun_slide1_title",
      descriptionKey: "firstrun_slide1_description",
    },
    {
      image: "/assets/images/03.gif",
      titleKey: "firstrun_slide2_title",
      descriptionKey: "firstrun_slide2_description",
    },
    {
      image: "/assets/images/04.gif",
      titleKey: "firstrun_slide3_title",
      descriptionKey: "firstrun_slide3_description",
    },
    {
      image: "/assets/images/05.gif",
      titleKey: "firstrun_slide4_title",
      descriptionKey: "firstrun_slide4_description",
    },
    {
      image: "/assets/images/06.gif",
      titleKey: "firstrun_slide5_title",
      descriptionKey: "firstrun_slide5_description",
    },
  ];

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  const goToPrevious = () => {
    setCurrentSlide(prev => prev > 0 ? prev - 1 : slides.length - 1);
  };

  const goToNext = () => {
    setCurrentSlide(prev => prev < slides.length - 1 ? prev + 1 : 0);
  };

  const handleDontShowAgainChange = (checked: boolean) => {
    setDontShowAgain(checked);

    // Save checkbox state to localStorage
    try {
      localStorage.setItem('carbowaste-firstrun-dont-show-again', checked.toString());
      console.log(`FirstRunExperience: Saved "don't show again" preference: ${checked}`);
    } catch (error) {
      console.warn('Failed to save firstrun checkbox state to localStorage:', error);
    }
  };

  const handleImageError = React.useCallback((slideIndex: number) => {
    const imagePath = slides[slideIndex].image;
    console.error("❌ Failed to load image for slide:", slideIndex);
    console.error("   Path:", imagePath);
    console.error("   Full URL:", `${window.location.origin}${imagePath}`);
    console.error("   Window location:", window.location.href);

    // Try to fetch the image to see actual HTTP error
    fetch(`${window.location.origin}${imagePath}`)
      .then(response => {
        console.error(`   HTTP Status: ${response.status} ${response.statusText}`);
        if (!response.ok) {
          return response.text().then(text => {
            console.error("   Response body:", text.substring(0, 200));
          });
        }
      })
      .catch(error => {
        console.error("   Fetch error:", error);
      });

    setImageLoadError(prev => new Set(prev).add(slideIndex));
  }, [slides]);

  const handleImageLoad = React.useCallback((slideIndex: number) => {
    console.log("✅ Successfully loaded image for slide:", slideIndex, "Path:", slides[slideIndex].image);
    setImageLoadError(prev => {
      const newSet = new Set(prev);
      newSet.delete(slideIndex);
      return newSet;
    });
  }, [slides]);

  const handleComplete = async () => {
    // Prevent multiple rapid calls on macOS
    if (isMacOS && !isInitializedRef.current) {
      console.warn('🍎 [macOS] handleComplete called before initialization - ignoring');
      return;
    }

    // Save completion state to localStorage
    try {
      localStorage.setItem('carbowaste-firstrun-completed', 'true');
      console.log('FirstRunExperience: Saved completion status to localStorage');
    } catch (error) {
      console.warn('Failed to save firstrun completion status to localStorage:', error);
    }

    // For startup variant, check if user wants to disable future first-run experience
    if (variant === 'startup' && dontShowAgain) {
      await onDisable();
    } else {
      onComplete();
    }
  };

  const openModal = () => {
    // Jeśli dialog Office jest już otwarty, zamknij go najpierw
    if (isOfficeDialogOpen && officeDialogRef.current) {
      console.log('Closing existing Office dialog before opening new one');
      officeDialogRef.current.close();
      setIsOfficeDialogOpen(false);
      officeDialogRef.current = null;
      // Krótkie opóźnienie przed otwarciem nowego dialogu
      setTimeout(() => openNewDialog(), 100);
      return;
    }

    // Jeśli fallback modal jest otwarty, zamknij go
    if (isModalOpen) {
      setIsModalOpen(false);
    }

    openNewDialog();
  };

  const openNewDialog = () => {
    // Use Office Dialog API to open image viewer in a centered modal window
    const currentSlideData = slides[currentSlide];
    const imageUrl = encodeURIComponent(currentSlideData.image);
    const title = encodeURIComponent(t(currentSlideData.titleKey));
    const description = encodeURIComponent(t(currentSlideData.descriptionKey));

    // Debug: log actual translated title
    console.log('Raw title key:', currentSlideData.titleKey);
    console.log('Translated title:', t(currentSlideData.titleKey));
    console.log('Encoded title:', title);

    const dialogUrl = `${window.location.origin}/dialog/image-viewer.html?image=${imageUrl}&title=${title}&description=${description}`;

    console.log('Opening Office dialog with URL:', dialogUrl);

    if (Office?.context?.ui?.displayDialogAsync) {
      // Get optimal dialog size for current environment
      const { height: dialogHeight, width: dialogWidth } = getDialogSize();

      Office.context.ui.displayDialogAsync(
        dialogUrl,
        {
          height: dialogHeight,
          width: dialogWidth,
          displayInIframe: true,
          requireHTTPS: false,
          promptBeforeOpen: false
        },
        (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            const dialog = result.value;
            officeDialogRef.current = dialog;
            setIsOfficeDialogOpen(true);
            console.log('Dialog opened successfully');

            // Handle dialog messages
            dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg: any) => {
              console.log('Dialog message received:', arg.message);

              if (arg.message === 'close') {
                dialog.close();
                setIsOfficeDialogOpen(false);
                officeDialogRef.current = null;
              }
            });

            // Handle dialog events
            dialog.addEventHandler(Office.EventType.DialogEventReceived, (arg: any) => {
              console.log('Dialog event received:', arg.error);
              dialog.close();
              setIsOfficeDialogOpen(false);
              officeDialogRef.current = null;
            });
          } else {
            console.error('Failed to open dialog:', result.error);
            // Fallback to regular modal
            setIsModalOpen(true);
          }
        }
      );
    } else {
      console.warn('Office Dialog API not available, using fallback modal');
      // Fallback to regular modal
      setIsModalOpen(true);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const handleModalOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      closeModal();
    }
  };

  React.useEffect(() => {
    renderCountRef.current += 1;

    // Simplified debugging for macOS - only log warnings
    if (isMacOS && renderCountRef.current > 15) {
      console.warn(`🍎 [macOS] WARNING: FirstRunExperience has been rendered ${renderCountRef.current} times! Possible re-render loop.`);
    }

    // Standard tracking (reduced logging)
    if (renderCountRef.current <= 3) { // Only log first 3 renders
      console.log(`First-run experience: viewing slide ${currentSlide + 1} of ${slides.length}`);
    }

    // Check if image has error for current slide
    if (imageLoadError.has(currentSlide)) {
      console.error(`Image load error detected for slide ${currentSlide}`);
    }
  }, [currentSlide, slides.length, imageLoadError, isMacOS, isModalOpen, isOfficeDialogOpen]);

  // Handle Escape key to close modal
  React.useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (isModalOpen) {
          closeModal();
        }

        // Emergency escape for macOS - close any open dialogs
        if (isMacOS && event.ctrlKey && event.shiftKey) {
          console.log('🍎 [macOS] Emergency escape triggered');
          if (isOfficeDialogOpen && officeDialogRef.current) {
            try {
              officeDialogRef.current.close();
              setIsOfficeDialogOpen(false);
              officeDialogRef.current = null;
            } catch (error) {
              console.warn('Emergency escape error:', error);
            }
          }
        }
      }
    };

    document.addEventListener('keydown', handleEscape);

    if (isModalOpen) {
      // More gentle body overflow handling for Office Add-in environment
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';

      // Store original for cleanup
      return () => {
        document.removeEventListener('keydown', handleEscape);
        document.body.style.overflow = originalOverflow || 'unset';
      };
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isModalOpen, isMacOS, isOfficeDialogOpen]);

  // Cleanup effect for Office Dialog API - CRITICAL for macOS stability
  React.useEffect(() => {
    return () => {
      // Cleanup any open Office dialogs before component unmounts
      if (isOfficeDialogOpen && officeDialogRef.current) {
        console.log('FirstRunExperience: Cleaning up Office dialog on unmount');
        try {
          officeDialogRef.current.close();
        } catch (error) {
          console.warn('FirstRunExperience: Error closing dialog on cleanup:', error);
        }
        setIsOfficeDialogOpen(false);
        officeDialogRef.current = null;
      }
    };
  }, [isMacOS]); // Include isMacOS in dependencies

  return (
    <div className={styles.root}>
      {/* Carousel Content */}
      <div className={styles.carouselContainer}>
        <div className={styles.slide}>
          {/* Slide Title - at the top */}
          <Title1 className={styles.slideTitle}>
            {t(slides[currentSlide].titleKey)}
          </Title1>

          {/* Slide Image - carousel content with full width */}
          {imageLoadError.has(currentSlide) ? (
            <div className={styles.imagePlaceholder}>
              <Text>
                📱 {t(slides[currentSlide].titleKey)}<br />
                <span className={styles.placeholderSubtext}>
                  (Demo visualization)
                </span>
              </Text>
            </div>
          ) : (
            <div className={styles.imageContainer} onClick={openModal}>
              <img
                src={`${slides[currentSlide].image}?v=${Date.now()}`}
                alt={t(slides[currentSlide].titleKey)}
                className={`${styles.slideImage} ${styles.clickableImage}`}
                onError={() => handleImageError(currentSlide)}
                onLoad={() => handleImageLoad(currentSlide)}
              />
              <div className={styles.imageLabel}>
                {t("firstrun_click_to_enlarge") || "Klik i zobacz więcej"}
              </div>
            </div>
          )}
        </div>

        {/* Dots Navigation - with previous/next buttons */}
        <div className={styles.navigation}>
          {/* Previous Button */}
          <Button
            appearance="subtle"
            onClick={goToPrevious}
            className={styles.navButton}
            disabled={currentSlide === 0}
            icon={<ChevronLeft16Regular />}
          >
            {t("firstrun_previous")}
          </Button>

          {/* Dots Indicator - centered */}
          <div className={styles.dots}>
            {slides.map((_, index) => (
              <div
                key={index}
                className={`${styles.dot} ${index === currentSlide ? styles.activeDot : ""
                  }`}
                onClick={() => goToSlide(index)}
              />
            ))}
          </div>

          {/* Next Button */}
          <Button
            appearance="subtle"
            onClick={goToNext}
            className={styles.navButton}
            disabled={currentSlide === slides.length - 1}
            icon={<ChevronRight16Regular />}
            iconPosition="after"
          >
            {t("firstrun_next")}
          </Button>
        </div>

        {/* Slide Description - moved below navigation */}
        <div className={styles.slide}>
          <Text className={styles.slideDescription}>
            {t(slides[currentSlide].descriptionKey)}
          </Text>
        </div>

        {/* Get Started Section - placed below carousel */}
        <div className={styles.getStartedContainer}>
          {/* Checkbox to disable first-run experience - only show for startup variant */}
          {variant === 'startup' && (
            <div className={styles.checkboxContainer}>
              <Checkbox
                checked={dontShowAgain}
                onChange={(_, data) => handleDontShowAgainChange(!!data.checked)}
                className={styles.checkboxLabel}
                label={t("firstrun_dont_show_again")}
              />
            </div>
          )}

          {/* Get Started Button - text depends on variant */}
          <Button
            appearance="primary"
            onClick={handleComplete}
            className={styles.getStartedButton}
          >
            {variant === 'startup' ? t("firstrun_get_started") : t("firstrun_end")}
          </Button>
        </div>
      </div>

      {/* Image Modal/Popup */}
      {isModalOpen && (
        <div className={styles.modalOverlay} onClick={handleModalOverlayClick}>
          <div className={styles.modalContent}>
            <Button
              appearance="subtle"
              className={styles.closeButton}
              icon={<Dismiss16Regular />}
              onClick={closeModal}
              title="Zamknij"
            />
            <img
              src={`${slides[currentSlide].image}?v=${Date.now()}`}
              alt={t(slides[currentSlide].titleKey)}
              className={styles.modalImage}
            />
            <Title1 className={styles.modalTitle}>
              {t(slides[currentSlide].titleKey)}
            </Title1>
            <Text className={styles.modalDescription}>
              {t(slides[currentSlide].descriptionKey)}
            </Text>
          </div>
        </div>
      )}
    </div>
  );
};

export default FirstRunExperience;